// Client-side data layer for the Community Center. Reuses the existing
// InstantDB client SDK singleton (`db`, from ./db) for everything the
// declarative permission rules in instant.perms.ts already allow a
// signed-in user to do directly, and calls the dedicated
// api/community/[action].js server endpoint for everything that needs
// server-stamped trust (post/comment creation and edits, introduction
// requests and responses, admin close-out, and reading media bytes) — see
// the long comment block in that file and in instant.perms.ts for exactly
// why each operation is split the way it is.
//
// Every write here mirrors the same shape the server endpoint or the
// permission rules expect; nothing here invents a new field or a new
// write path beyond what those two files already define.

import { id } from '@instantdb/react'
import { db } from './db'
import { ADMIN_EMAILS, isAdminEmail } from './adminEmails'

export type PostArea = 'announcement' | 'conversation' | 'win'

export interface CommunityMemberRow {
  id: string
  userId: string
  displayName: string
  avatarUrl: string
  shortIntro: string
  guidelinesAcknowledgedAt: number | null
  suspended: boolean
  notificationsEnabled: boolean
  createdAt: number
  updatedAt: number | null
}

export interface CommunityPostRow {
  id: string
  area: PostArea
  authorId: string
  authorDisplayName: string
  content: string
  mediaType: 'photo' | 'video' | null
  mediaPath: string | null
  videoDurationSeconds: number | null
  commentsDisabled: boolean
  hiddenByAdmin: boolean
  removedByAuthor: boolean
  createdAt: number
  updatedAt: number | null
  editWindowExpiresAt: number | null
}

export interface CommunityCommentRow {
  id: string
  postId: string
  parentCommentId: string | null
  authorId: string
  authorDisplayName: string
  content: string
  hiddenByAdmin: boolean
  createdAt: number
  updatedAt: number | null
  editWindowExpiresAt: number | null
}

export interface CommunityReactionRow {
  id: string
  targetType: 'post' | 'comment'
  targetId: string
  userId: string
  emoji: string
  createdAt: number
}

export interface CommunityReportRow {
  id: string
  targetType: 'post' | 'comment'
  targetId: string
  reporterId: string
  reason: string
  resolved: boolean
  createdAt: number
}

export interface CommunityIntroductionRequestRow {
  id: string
  requesterId: string
  recipientId: string
  note: string
  status: 'pending' | 'accepted' | 'declined' | 'closed'
  createdAt: number
  respondedAt: number | null
  closedAt: number | null
  closedReason: string
  adminAllowedAgainAt: number | null
}

function mapMember(row: any): CommunityMemberRow {
  return {
    id: row.id,
    userId: row.userId,
    displayName: row.displayName || '',
    avatarUrl: row.avatarUrl || '',
    shortIntro: row.shortIntro || '',
    guidelinesAcknowledgedAt: row.guidelinesAcknowledgedAt ? Number(row.guidelinesAcknowledgedAt) : null,
    suspended: !!row.suspended,
    notificationsEnabled: row.notificationsEnabled !== false,
    createdAt: Number(row.createdAt) || 0,
    updatedAt: row.updatedAt ? Number(row.updatedAt) : null,
  }
}

function mapPost(row: any): CommunityPostRow {
  return {
    id: row.id,
    area: row.area,
    authorId: row.authorId,
    authorDisplayName: row.authorDisplayName || 'Member',
    content: row.content || '',
    mediaType: row.mediaType || null,
    mediaPath: row.mediaPath || null,
    videoDurationSeconds: row.videoDurationSeconds ?? null,
    commentsDisabled: !!row.commentsDisabled,
    hiddenByAdmin: !!row.hiddenByAdmin,
    removedByAuthor: !!row.removedByAuthor,
    createdAt: Number(row.createdAt) || 0,
    updatedAt: row.updatedAt ? Number(row.updatedAt) : null,
    editWindowExpiresAt: row.editWindowExpiresAt ? Number(row.editWindowExpiresAt) : null,
  }
}

function mapComment(row: any): CommunityCommentRow {
  return {
    id: row.id,
    postId: row.postId,
    parentCommentId: row.parentCommentId || null,
    authorId: row.authorId,
    authorDisplayName: row.authorDisplayName || 'Member',
    content: row.content || '',
    hiddenByAdmin: !!row.hiddenByAdmin,
    createdAt: Number(row.createdAt) || 0,
    updatedAt: row.updatedAt ? Number(row.updatedAt) : null,
    editWindowExpiresAt: row.editWindowExpiresAt ? Number(row.editWindowExpiresAt) : null,
  }
}

function mapReaction(row: any): CommunityReactionRow {
  return {
    id: row.id,
    targetType: row.targetType,
    targetId: row.targetId,
    userId: row.userId,
    emoji: row.emoji,
    createdAt: Number(row.createdAt) || 0,
  }
}

function mapReport(row: any): CommunityReportRow {
  return {
    id: row.id,
    targetType: row.targetType,
    targetId: row.targetId,
    reporterId: row.reporterId,
    reason: row.reason || '',
    resolved: !!row.resolved,
    createdAt: Number(row.createdAt) || 0,
  }
}

function mapIntroductionRequest(row: any): CommunityIntroductionRequestRow {
  return {
    id: row.id,
    requesterId: row.requesterId,
    recipientId: row.recipientId,
    note: row.note || '',
    status: row.status,
    createdAt: Number(row.createdAt) || 0,
    respondedAt: row.respondedAt ? Number(row.respondedAt) : null,
    closedAt: row.closedAt ? Number(row.closedAt) : null,
    closedReason: row.closedReason || '',
    adminAllowedAgainAt: row.adminAllowedAgainAt ? Number(row.adminAllowedAgainAt) : null,
  }
}

// ---------------------------------------------------------------------------
// Server action helper — mirrors adminMessagesRequest/adminCheckInsRequest's
// pattern in instantData.ts: the caller's own InstantDB session token
// (db.useAuth().user.refresh_token) is sent as a bearer token, verified
// server-side. It carries no elevated privilege by itself.
// ---------------------------------------------------------------------------

async function communityActionRequest(
  action: string,
  token: string,
  init?: RequestInit
) {
  const res = await fetch(`/api/community/${action}`, {
    method: 'POST',
    ...init,
    headers: {
      ...(init?.headers || {}),
      Authorization: `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  })
  const body = await res.json().catch(() => ({}))
  if (!res.ok) {
    const err: any = new Error(body?.error || `Request failed (${res.status})`)
    err.code = body?.code
    throw err
  }
  return body
}

// ---------------------------------------------------------------------------
// Member profile + Community Promise
// ---------------------------------------------------------------------------

/** The signed-in user's own community profile row, or null if they haven't set one up yet. */
export async function fetchMyCommunityMember(userId: string): Promise<CommunityMemberRow | null> {
  const { data } = await db.queryOnce({ communityMembers: { $: { where: { userId } } } })
  const row = data.communityMembers?.[0]
  return row ? mapMember(row) : null
}

/**
 * Creates or updates the signed-in user's own profile fields. Never touches
 * `suspended` or `guidelinesAcknowledgedAt` — the permission rules block a
 * non-admin from setting `suspended` on create, and restrict update to this
 * exact field list (see instant.perms.ts), so this function only ever
 * writes fields it's actually allowed to.
 */
export async function upsertCommunityProfile(
  userId: string,
  fields: { displayName?: string; avatarUrl?: string; shortIntro?: string; notificationsEnabled?: boolean }
) {
  const { data } = await db.queryOnce({ communityMembers: { $: { where: { userId } } } })
  const existing = data.communityMembers?.[0]
  const now = Date.now()

  if (existing) {
    await db.transact(db.tx.communityMembers[existing.id].update({ ...fields, updatedAt: now }))
    return existing.id
  }

  const memberId = id()
  await db.transact(
    db.tx.communityMembers[memberId]
      .update({ userId, ...fields, createdAt: now, updatedAt: now })
      .link({ user: userId })
  )
  return memberId
}

/**
 * Records acceptance of the Community Promise — the one-time gate that
 * unlocks posting/commenting/reacting. Creates the member profile row first
 * if the person hasn't set one up at all yet.
 */
export async function acceptCommunityPromise(userId: string) {
  const { data } = await db.queryOnce({ communityMembers: { $: { where: { userId } } } })
  const existing = data.communityMembers?.[0]
  const now = Date.now()

  if (existing) {
    await db.transact(
      db.tx.communityMembers[existing.id].update({ guidelinesAcknowledgedAt: now, updatedAt: now })
    )
    return existing.id
  }

  const memberId = id()
  await db.transact(
    db.tx.communityMembers[memberId]
      .update({ userId, guidelinesAcknowledgedAt: now, createdAt: now, updatedAt: now })
      .link({ user: userId })
  )
  return memberId
}

/**
 * Other eligible members to introduce yourself to: anyone (besides you) who
 * has accepted the Community Promise and isn't suspended. Cassie's own
 * admin account is deliberately excluded from this list — clients reach her
 * directly through Message Cassie, not through a peer-to-peer introduction
 * request. This can't verify each listed member is *still* an active client
 * (that status lives on userPackages, not on this row, and there's no
 * schema link to join it client-side) — the request-introduction server
 * action re-checks that live, authoritatively, before a request is
 * actually created.
 */
/**
 * Cassie's own $users id, so "Connect With Cassie" can send an introduction
 * request straight to her the exact same way a member-to-member request
 * works — no new table, no new permission. Reuses the existing $users view
 * rule (any signed-in user can already read $users), same as the admin
 * exclusion check above.
 */
export async function fetchAdminUserId(): Promise<string | null> {
  const { data } = await db.queryOnce({ $users: { $: { where: { email: ADMIN_EMAILS[0] } } } })
  return data.$users?.[0]?.id || null
}

export async function fetchIntroducibleMembers(excludeUserId: string): Promise<CommunityMemberRow[]> {
  const { data } = await db.queryOnce({ communityMembers: { user: {} } })
  return (data.communityMembers || [])
    .filter((row: any) => !isAdminEmail(row.user?.email))
    .map(mapMember)
    .filter((m) => m.userId !== excludeUserId && !!m.guidelinesAcknowledgedAt && !m.suspended)
    .sort((a, b) => a.displayName.localeCompare(b.displayName))
}

// ---------------------------------------------------------------------------
// Admin: members + moderation
// ---------------------------------------------------------------------------

export async function fetchAllCommunityMembersAdmin(): Promise<CommunityMemberRow[]> {
  const { data } = await db.queryOnce({ communityMembers: {} })
  return (data.communityMembers || []).map(mapMember).sort((a, b) => b.createdAt - a.createdAt)
}

export async function setCommunityMemberSuspended(memberId: string, suspended: boolean) {
  await db.transact(db.tx.communityMembers[memberId].update({ suspended, updatedAt: Date.now() }))
}

// ---------------------------------------------------------------------------
// Posts
// ---------------------------------------------------------------------------

/** Feed for one area. The view permission rule already hides admin-hidden/author-removed posts from non-admins — nothing to filter here. */
export async function fetchCommunityPosts(area: PostArea): Promise<CommunityPostRow[]> {
  const { data } = await db.queryOnce({ communityPosts: { $: { where: { area } } } })
  return (data.communityPosts || []).map(mapPost).sort((a, b) => b.createdAt - a.createdAt)
}

/** Admin moderation view: every post in every area, hidden/removed included. */
export async function fetchAllCommunityPostsAdmin(): Promise<CommunityPostRow[]> {
  const { data } = await db.queryOnce({ communityPosts: {} })
  return (data.communityPosts || []).map(mapPost).sort((a, b) => b.createdAt - a.createdAt)
}

export async function createCommunityPost(
  token: string,
  input: {
    area: PostArea
    content?: string
    mediaType?: 'photo' | 'video'
    mediaPath?: string
    videoDurationSeconds?: number
    commentsDisabled?: boolean
  }
): Promise<string> {
  const body = await communityActionRequest('create-post', token, { body: JSON.stringify(input) })
  return body.postId as string
}

export async function editCommunityPost(
  token: string,
  input: { postId: string; content?: string; commentsDisabled?: boolean }
) {
  await communityActionRequest('edit-post', token, { body: JSON.stringify(input) })
}

/** The author's own soft-delete — allowed directly by the permission rules, no server round-trip needed. */
export async function removeOwnCommunityPost(postId: string) {
  await db.transact(db.tx.communityPosts[postId].update({ removedByAuthor: true, updatedAt: Date.now() }))
}

export async function setCommunityPostHidden(postId: string, hidden: boolean) {
  await db.transact(db.tx.communityPosts[postId].update({ hiddenByAdmin: hidden, updatedAt: Date.now() }))
}

/**
 * Admin-only PERMANENT delete — distinct from the soft actions above.
 * removeOwnCommunityPost/setCommunityPostHidden only ever flip a flag, so the
 * row (and its media/comments) survives forever as a moderation record,
 * visible to admin only. This actually removes the post row, plus every
 * comment, reaction, and report that references it, so nothing is left
 * orphaned behind it. Allowed directly by the existing permission rules
 * (auth.email == admin on communityPosts/communityComments/
 * communityReactions/communityReports delete) — no schema or permission
 * change needed. Irreversible: callers must confirm with the admin first.
 */
export async function adminDeleteCommunityPost(postId: string): Promise<void> {
  const [comments, postReactions, allReports] = await Promise.all([
    fetchCommentsForPost(postId),
    fetchReactionsFor('post', [postId]),
    fetchAllCommunityReportsAdmin(),
  ])
  const commentIds = comments.map((c) => c.id)
  const commentReactions = commentIds.length ? await fetchReactionsFor('comment', commentIds) : []
  const targetIds = new Set([postId, ...commentIds])
  const relatedReports = allReports.filter((r) => targetIds.has(r.targetId))

  await db.transact([
    ...postReactions.map((r) => db.tx.communityReactions[r.id].delete()),
    ...commentReactions.map((r) => db.tx.communityReactions[r.id].delete()),
    ...relatedReports.map((r) => db.tx.communityReports[r.id].delete()),
    ...commentIds.map((id) => db.tx.communityComments[id].delete()),
    db.tx.communityPosts[postId].delete(),
  ])
}

// ---------------------------------------------------------------------------
// Comments
// ---------------------------------------------------------------------------

/** All comments for one post (top-level and replies together); the view rule already hides comments on a hidden/removed post from non-admins. */
export async function fetchCommentsForPost(postId: string): Promise<CommunityCommentRow[]> {
  const { data } = await db.queryOnce({ communityComments: { $: { where: { postId } } } })
  return (data.communityComments || []).map(mapComment).sort((a, b) => a.createdAt - b.createdAt)
}

export async function createCommunityComment(
  token: string,
  input: { postId: string; content: string; parentCommentId?: string }
): Promise<string> {
  const body = await communityActionRequest('create-comment', token, { body: JSON.stringify(input) })
  return body.commentId as string
}

export async function editCommunityComment(token: string, input: { commentId: string; content: string }) {
  await communityActionRequest('edit-comment', token, { body: JSON.stringify(input) })
}

/** A comment's own delete is a true row delete, available to its author any time (see instant.perms.ts). */
export async function deleteOwnCommunityComment(commentId: string) {
  await db.transact(db.tx.communityComments[commentId].delete())
}

export async function setCommunityCommentHidden(commentId: string, hidden: boolean) {
  await db.transact(db.tx.communityComments[commentId].update({ hiddenByAdmin: hidden, updatedAt: Date.now() }))
}

// ---------------------------------------------------------------------------
// Reactions — a small fixed set of reaction emoji, one row per
// (user, target, emoji), toggled on/off. Kept deliberately simple (no
// custom-emoji picker) to stay within "required launch functionality only."
// ---------------------------------------------------------------------------

export const COMMUNITY_REACTION_EMOJI = ['👍', '💪', '❤️'] as const

export async function fetchReactionsFor(
  targetType: 'post' | 'comment',
  targetIds: string[]
): Promise<CommunityReactionRow[]> {
  if (!targetIds.length) return []
  const { data } = await db.queryOnce({ communityReactions: { $: { where: { targetType } } } })
  const idSet = new Set(targetIds)
  return (data.communityReactions || [])
    .filter((r: any) => idSet.has(r.targetId))
    .map(mapReaction)
}

/** Toggles the signed-in user's own reaction of this emoji on a target. Blocked for suspended members by the permission rules themselves. */
export async function toggleCommunityReaction(
  userId: string,
  targetType: 'post' | 'comment',
  targetId: string,
  emoji: string
) {
  const { data } = await db.queryOnce({
    communityReactions: { $: { where: { targetType, targetId, userId, emoji } } },
  })
  const existing = data.communityReactions?.[0]
  if (existing) {
    await db.transact(db.tx.communityReactions[existing.id].delete())
    return false
  }
  const reactionId = id()
  await db.transact(
    db.tx.communityReactions[reactionId].update({ targetType, targetId, userId, emoji, createdAt: Date.now() })
  )
  return true
}

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export async function createCommunityReport(
  reporterId: string,
  targetType: 'post' | 'comment',
  targetId: string,
  reason?: string
) {
  const reportId = id()
  await db.transact(
    db.tx.communityReports[reportId].update({
      targetType,
      targetId,
      reporterId,
      reason: reason || '',
      createdAt: Date.now(),
    })
  )
}

export async function fetchAllCommunityReportsAdmin(): Promise<CommunityReportRow[]> {
  const { data } = await db.queryOnce({ communityReports: {} })
  return (data.communityReports || [])
    .map(mapReport)
    .sort((a, b) => Number(a.resolved) - Number(b.resolved) || b.createdAt - a.createdAt)
}

export async function resolveCommunityReport(reportId: string) {
  await db.transact(db.tx.communityReports[reportId].update({ resolved: true }))
}

// ---------------------------------------------------------------------------
// Cassie-mediated introductions — create, respond, and admin close-out all
// go through the server endpoint (see api/community/[action].js); nothing
// here writes communityIntroductionRequests directly.
// ---------------------------------------------------------------------------

/**
 * Shared exact copy used when a member asks Cassie to help find a
 * connection (no other members available yet) rather than requesting
 * Cassie herself for a direct chat, or requesting a specific member. There
 * is no separate "request type" field in the schema — all three are the
 * same communityIntroductionRequests row shape (requesterId/recipientId/
 * note) — so this string is the one signal that distinguishes "help me
 * find someone" from a plain direct request to Cassie, in the admin
 * Introductions view. Kept as one shared constant so the composer that
 * writes it and the admin view that reads it never drift apart.
 */
export const HELP_FIND_CONNECTION_NOTE = "I'd like help finding a connection to another member."

export async function requestCommunityIntroduction(
  token: string,
  input: { recipientId: string; note?: string }
) {
  await communityActionRequest('request-introduction', token, { body: JSON.stringify(input) })
}

export async function respondToCommunityIntroduction(
  token: string,
  input: { requestId: string; response: 'accepted' | 'declined' }
) {
  await communityActionRequest('respond-introduction', token, { body: JSON.stringify(input) })
}

export async function closeCommunityIntroduction(
  token: string,
  input: { requestId: string; closedReason?: string; adminAllowedAgainAt?: number }
) {
  await communityActionRequest('close-introduction', token, { body: JSON.stringify(input) })
}

/** Every introduction request involving this user, either side, most recent first. */
export async function fetchMyIntroductionRequests(userId: string): Promise<CommunityIntroductionRequestRow[]> {
  const { data } = await db.queryOnce({
    communityIntroductionRequests: {
      $: { where: { or: [{ requesterId: userId }, { recipientId: userId }] } },
    },
  })
  return (data.communityIntroductionRequests || [])
    .map(mapIntroductionRequest)
    .sort((a, b) => b.createdAt - a.createdAt)
}

export async function fetchAllIntroductionRequestsAdmin(): Promise<CommunityIntroductionRequestRow[]> {
  const { data } = await db.queryOnce({ communityIntroductionRequests: {} })
  return (data.communityIntroductionRequests || [])
    .map(mapIntroductionRequest)
    .sort((a, b) => b.createdAt - a.createdAt)
}

// ---------------------------------------------------------------------------
// Media — upload goes straight through the client SDK (same pattern as
// uploadProgressPhoto); display always goes through the authenticated
// /api/community/media proxy (see CommunityMedia.tsx), never a raw URL.
// ---------------------------------------------------------------------------

export async function uploadCommunityMedia(userId: string, file: File): Promise<string> {
  const path = `community-media/${userId}/${Date.now()}-${file.name}`
  await db.storage.uploadFile(path, file, { contentType: file.type })
  await db.queryOnce({ $files: { $: { where: { path } } } })
  return path
}

export function communityMediaRequestUrl(postId: string): string {
  return `/api/community/media?postId=${encodeURIComponent(postId)}`
}
